import React, { useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { Check as CheckIcon, Clear as ClearIcon } from "@mui/icons-material";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import toast from "react-hot-toast";

/**
 * GroupRequest component for handling group request notifications
 * Displays accept/reject buttons for unread group request notifications
 * 
 * @param {Object} notification - The notification object containing group request details
 * @param {Function} onActionComplete - Callback function called after accept/reject action
 */
const GroupRequest = ({ notification, onActionComplete }) => {
  // Use separate loading states for accept and reject actions
  const [loadingStates, setLoadingStates] = useState({});
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Get request ID from meta - this is the actual request ID from the database
  // The meta.requestId should match the request._id from the requests API
  const requestId = notification?.meta?.requestId;
  // Get conversation ID from meta
  const conversationId = notification?.meta?.conversationId;
  
  // Debug: Log notification structure to help identify issues
  if (!requestId || !conversationId) {
    console.warn("Notification missing required fields:", {
      notificationId: notification?._id,
      hasRequestId: !!requestId,
      hasConversationId: !!conversationId,
      meta: notification?.meta,
      fullNotification: notification,
    });
  }

  // Handle accept group request
  const handleAccept = async () => {
    // Validate required fields
    if (!conversationId) {
      toast.error("conversation information is missing");
      console.error("Missing conversationId in notification:", notification);
      return;
    }

    if (!requestId) {
      toast.error("request information is missing");
      console.error("Missing requestId in notification:", notification);
      return;
    }

    // Set loading state for accept action
    setLoadingStates((prev) => ({ ...prev, [`accept_${requestId}`]: true }));

    try {
      // Log the data being sent for debugging
      console.log("Accepting group request:", {
        conversationId,
        requestId,
        notificationId: notification._id,
        fullNotification: notification,
      });

      const response = await axiosInstance.post(
        `${ApiEndPoints.CHAT.acceptRequest}/${conversationId}`,
        {
          requestId: requestId,
        }
      );

      // Check response structure - handle both response.data.data and response.data
      const success = response.data?.data?.success || response.data?.success;
      const message = response.data?.data?.message || response.data?.message;

      if (success) {
        toast.success("group request accepted");
        
        // Navigate to the chat page with the conversation ID
        if (conversationId) {
          navigate(`/chats/${conversationId}`);
        }
        
        // Backend handles marking notification as read automatically
        if (onActionComplete) {
          onActionComplete();
        }
        // Invalidate notifications query to refresh the list
        queryClient.invalidateQueries(["notifications"]);
        queryClient.invalidateQueries(["notifications-count"]);
      } else {
        throw new Error(message || "failed to accept request");
      }
    } catch (error) {
      console.error("Error accepting group request:", error);
      console.error("Error response:", error?.response?.data);
      
      // Extract error message from various possible locations
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.data?.message ||
        error?.message ||
        "failed to accept request. please try again.";
      
      toast.error(errorMessage);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [`accept_${requestId}`]: false }));
    }
  };

  // Handle reject group request
  const handleReject = async () => {
    // Validate required fields
    if (!conversationId) {
      toast.error("conversation information is missing");
      console.error("Missing conversationId in notification:", notification);
      return;
    }

    if (!requestId) {
      toast.error("request information is missing");
      console.error("Missing requestId in notification:", notification);
      return;
    }

    // Set loading state for reject action
    setLoadingStates((prev) => ({ ...prev, [`reject_${requestId}`]: true }));

    try {
      // Log the data being sent for debugging
      console.log("Rejecting group request:", {
        conversationId,
        requestId,
        notificationId: notification._id,
        fullNotification: notification,
      });

      const response = await axiosInstance.post(
        `${ApiEndPoints.CHAT.rejectRequest}/${conversationId}`,
        {
          requestId: requestId,
        }
      );

      // Check response structure - handle both response.data.data and response.data
      const success = response.data?.data?.success || response.data?.success;
      const message = response.data?.data?.message || response.data?.message;

      if (success) {
        toast.success("group request rejected");
        
        // Backend handles marking notification as read automatically
        if (onActionComplete) {
          onActionComplete();
        }
        // Invalidate notifications query to refresh the list
        queryClient.invalidateQueries(["notifications"]);
        queryClient.invalidateQueries(["notifications-count"]);
      } else {
        throw new Error(message || "failed to reject request");
      }
    } catch (error) {
      console.error("Error rejecting group request:", error);
      console.error("Error response:", error?.response?.data);
      
      // Extract error message from various possible locations
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.data?.message ||
        error?.message ||
        "failed to reject request. please try again.";
      
      toast.error(errorMessage);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [`reject_${requestId}`]: false }));
    }
  };

  // Get loading states for this specific request
  const isAcceptLoading = loadingStates[`accept_${requestId}`] || false;
  const isRejectLoading = loadingStates[`reject_${requestId}`] || false;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        mt: 1.5,
        justifyContent: "flex-end",
      }}
    >
      <Button
        variant="outlined"
        color="error"
        size="small"
        startIcon={
          isRejectLoading ? <CircularProgress size={14} /> : <ClearIcon />
        }
        disabled={isRejectLoading || isAcceptLoading}
        onClick={handleReject}
        sx={{
          minWidth: "auto",
          px: 2,
          py: 0.5,
          fontSize: "0.75rem",
          textTransform: "lowercase",
        }}
      >
        {isRejectLoading ? "rejecting..." : "reject"}
      </Button>
      <Button
        variant="contained"
        color="success"
        size="small"
        startIcon={
          isAcceptLoading ? <CircularProgress size={14} /> : <CheckIcon />
        }
        disabled={isAcceptLoading || isRejectLoading}
        onClick={handleAccept}
        sx={{
          minWidth: "auto",
          px: 2,
          py: 0.5,
          fontSize: "0.75rem",
          textTransform: "lowercase",
        }}
      >
        {isAcceptLoading ? "accepting..." : "accept"}
      </Button>
    </Box>
  );
};

export default GroupRequest;

